import {getCountryCodeForRegionCode, parsePhoneNumber} from 'awesome-phonenumber'
import cn from 'classnames'
import {useField, useFormikContext} from 'formik'
import {getAlpha2Codes} from 'i18n-iso-countries'
import _ from 'lodash'
import {ClipboardEvent, FC, FormEvent, useEffect, useRef, useState} from 'react'
import PhoneInput from 'react-phone-input-2'

import {usePreferences} from '@/components/context/Preferences'
import {useCommonContent} from '@/content/common'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {euCountriesCodes, localeToNationalCountry} from '@/utils/constants'
import {alpha2ToName} from '@/utils/countries/translations'
import {normalizeStoredPhoneNumber} from '@/utils/formatting/phone-number'
import {useLanguage} from '@/utils/i18n/language'

//Copied from react-phone-input-2 lib since the type is not exported
type CountryData = {
  name: string
  dialCode: string
  countryCode: string
  format: string
}

type PhoneNumberProps = {
  name: string
  disabled?: boolean
  dataTest?: string
  className?: string
}

export const PhoneNumberField: FC<PhoneNumberProps> = ({name, disabled, dataTest, className}) => {
  const language = useLanguage()
  const {t} = useTranslation()

  const [field, helpers, {setValue}] = useField<string>(name)
  const {isSubmitting} = useFormikContext()
  const phoneInputRef = useRef<HTMLInputElement>(null)

  const {countrySearchPlaceholder} = useCommonContent()
  const {country: detectedCountry} = usePreferences()

  const countryBasedOnLanguage = localeToNationalCountry[language]

  const countries = _(getAlpha2Codes())
    .toPairs()
    .map(([code]) => [code.toLowerCase(), alpha2ToName(code, language)])
    .fromPairs()
    .value()

  const detectedCountryCode = detectedCountry?.toLowerCase()
  const defaultCountry =
    detectedCountryCode && countries[detectedCountryCode]
      ? detectedCountryCode
      : countryBasedOnLanguage

  const preferredCountries = _.uniq([defaultCountry, countryBasedOnLanguage]).concat(
    _(countries)
      .pickBy((_, code) => euCountriesCodes.includes(code.toUpperCase()))
      .keys()
      .without(defaultCountry, countryBasedOnLanguage)
      .value(),
  )

  const initialValue = normalizeStoredPhoneNumber(field.value ?? '')
  const initialParsedPhoneNumber = parsePhoneNumber(initialValue, {
    regionCode: defaultCountry.toUpperCase(),
  })

  // save selected country in state, so we can reset the number when the country changes
  const [country, setCountry] = useState(
    initialParsedPhoneNumber?.regionCode?.toLowerCase() ?? defaultCountry,
  )

  // save current input value in state, so we can reset it to an empty string when the country changes
  const [inputValue, setInputValue] = useState(
    initialParsedPhoneNumber?.number?.e164 ?? initialValue,
  )

  // used to re-render of the inner PhoneInput component
  const [key, setKey] = useState(0)

  useEffect(() => {
    if (field.value !== inputValue) {
      // only if this phone number is being changed from outside. A number stored before E.164 shows
      // normalised but stays as stored (the schema normalises it), so loading a form changes nothing
      const stored = normalizeStoredPhoneNumber(field.value ?? '')
      const pn = parsePhoneNumber(stored, {
        regionCode: defaultCountry.toUpperCase(),
      })
      const shown = pn?.number?.e164 ?? stored
      if (shown !== inputValue) {
        setCountry(pn?.regionCode?.toLowerCase() ?? defaultCountry)
        setInputValue(shown)
        // force re-render of the inner PhoneInput component
        setKey(key + 1)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value])

  // Static pages read the detected country after mount; adopt it while the field is empty
  useEffect(() => {
    if (!inputValue && country !== defaultCountry) {
      setCountry(defaultCountry)
      setKey(key + 1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCountry])

  const isDisabled = isSubmitting || disabled
  const hasError = !!helpers.error && helpers.touched

  // the re-mount drops focus, so a replaced number asks for it back, with the caret at its end
  const focusAfterReplace = useRef(false)

  useEffect(() => {
    const input = phoneInputRef.current
    if (focusAfterReplace.current && input) {
      focusAfterReplace.current = false
      input.focus()
      input.setSelectionRange(input.value.length, input.value.length)
    }
  }, [key])

  // PhoneInput resets its number when the country prop changes, and blanks out on an empty value
  // instead of showing the dial code, so a number set from here re-mounts it
  const replaceNumber = (nextCountry: string, value: string) => {
    setCountry(nextCountry)
    setInputValue(value)
    setValue(value)
    setKey(key + 1)
    focusAfterReplace.current = document.activeElement === phoneInputRef.current
  }

  // PhoneInput appends a paste to the dial code it already shows, so a copied `+421 905 123 456`
  // turns into `+44 421 905 ...`, and drops an autofill that doesn't start with that dial code.
  // A whole number replaces the number instead.
  const replaceWithWholeNumber = (text: string) => {
    const pn = parsePhoneNumber(text, {regionCode: country.toUpperCase()})
    if (!pn.valid) {
      return false
    }
    replaceNumber(pn.regionCode.toLowerCase(), pn.number.e164)
    return true
  }

  // The dial code alone looks the same as an empty field, so it's stored as no number
  const clearNumber = () => replaceNumber(country, '')

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    if (replaceWithWholeNumber(e.clipboardData.getData('text'))) {
      e.preventDefault()
    }
  }

  const handleInput = (e: FormEvent<HTMLInputElement>) => {
    const {value} = e.currentTarget
    if (!/\d/.test(value)) {
      // PhoneInput ignores edits that remove its dial code, so cmd+backspace, select-all + delete
      // or cut did nothing
      clearNumber()
    } else if (!value.startsWith(`+${getCountryCodeForRegionCode(country.toUpperCase())}`)) {
      replaceWithWholeNumber(value)
    }
  }

  // Chrome ignores autocomplete="off" and offers saved addresses on the country search box, as its
  // placeholder reads like an address field. An unknown autocomplete token turns that off.
  useEffect(() => {
    const container = phoneInputRef.current?.parentElement
    const handleFocusIn = (e: FocusEvent) => {
      if (e.target instanceof HTMLInputElement && e.target.type === 'search') {
        e.target.setAttribute('autocomplete', 'search-filter')
      }
    }
    container?.addEventListener('focusin', handleFocusIn)
    return () => container?.removeEventListener('focusin', handleFocusIn)
  }, [key])

  // Ensures the country flag dropdown button inside the phone input is skipped in the tab order
  useEffect(() => {
    const flagButton = phoneInputRef.current?.parentElement?.querySelector('.selected-flag')
    if (flagButton) {
      flagButton.setAttribute('tabindex', '-1')
    }
  }, [])

  return (
    <>
      <PhoneInput
        key={key} // re-render on change of the input value programatically
        data-test={dataTest}
        {...field}
        onChange={(value, countryData: CountryData, e, formattedNumber) => {
          const pn = parsePhoneNumber(formattedNumber)
          const pv =
            value === countryData.dialCode ? '' : pn?.number?.e164 ?? `+${value.replace(/\D/g, '')}`
          if (country !== countryData.countryCode || !pv) {
            // A new flag keeps the digits, which PhoneInput moves over to its dial code. That's a
            // picked flag, or one guessed from a shared dial code (+1 416 is Canada).
            replaceNumber(countryData.countryCode, pv)
          } else {
            setInputValue(pv)
            setValue(pv)
          }
        }}
        value={inputValue}
        disabled={isDisabled}
        inputProps={{
          name,
          tabIndex: 0,
          ref: phoneInputRef,
          autoComplete: 'tel',
          onPaste: handlePaste,
          onInput: handleInput,
        }}
        country={country}
        preferredCountries={preferredCountries}
        onlyCountries={Object.keys(countries)}
        preserveOrder={['preferredCountries', 'onlyCountries']}
        localization={countries}
        countryCodeEditable={false}
        // PhoneInput moves the caret to the end on every focus; only an empty field needs that, so
        // typing starts after the dial code. A click into a number keeps the caret where it lands.
        jumpCursorToEnd={!inputValue}
        // The format mask has no slot for a typed national 0 (`07946 095800`) and would cut off the
        // last digit. The 0 goes once the number parses.
        enableLongNumbers
        enableSearch
        disableSearchIcon
        searchPlaceholder={t(countrySearchPlaceholder)}
        specialLabel=""
        containerClass={cn(
          isDisabled && 'disabled',
          hasError && 'error',
          `selected-${country}`,
          className,
        )}
        prefix="+"
      />

      <style jsx global>{`
        .react-tel-input {
          position: relative;
          display: flex;
          flex-direction: row-reverse;
          border-radius: 8px;
        }
        .react-tel-input .form-control {
          background-color: ${theme.palette.background.default};
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[300]};
          ${theme.typography.text16.desktop};
          color: ${theme.palette.text.default};
          width: 100%;
          padding: ${theme.spacing[2]} 12px;
          margin-left: -1px;
          border-radius: 0px 8px 8px 0px;
        }
        .react-tel-input .form-control:focus,
        .react-tel-input .form-control:focus + .flag-dropdown,
        .react-tel-input input.open,
        .react-tel-input input.open + .flag-dropdown {
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[900]};
        }
        .react-tel-input:not(.disabled, .error):focus-within {
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[900]},
            0px 0px 0px 4px ${theme.palette.gray[100]};
        }
        .react-tel-input .form-control:disabled {
          color: ${theme.palette.gray[400]};
          -webkit-text-fill-color: ${inputValue
            ? theme.palette.text.default
            : theme.palette.gray[400]};
          opacity: 1;
          background-color: ${theme.palette.gray[200]};
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[200]};
        }
        .react-tel-input .form-control:disabled + .flag-dropdown {
          background-color: ${theme.palette.gray[200]};
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[200]};
          cursor: default;
        }
        .react-tel-input.disabled .selected-flag {
          border-right: 1px solid ${theme.palette.gray[300]};
        }
        .react-tel-input.error .form-control,
        .react-tel-input.error .flag-dropdown {
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.red[700]};
        }
        .react-tel-input.error:focus-within .form-control,
        .react-tel-input.error:focus-within .flag-dropdown {
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.error.main},
            0px 0px 0px 2px ${theme.palette.red[200]};
        }
        .react-tel-input .flag-dropdown {
          background-color: ${theme.palette.background.default};
          box-shadow: inset 0px 0px 0px 1px ${theme.palette.gray[300]};
          ${theme.typography.text16.desktop};
          border-radius: 8px 0px 0px 8px;
          cursor: pointer;
        }
        .react-tel-input .selected-flag {
          position: relative;
          display: flex;
          align-items: center;
          width: 73px;
          height: 100%;
          padding: ${theme.spacing[2]} ${theme.spacing[2.5]};
        }
        .react-tel-input .flag {
          width: 24px;
          height: 24px;
          flex: 0 0 24px;
          background: url('/images/flags.png');
          background-repeat: no-repeat;
          border-radius: 10px;
        }
        .react-tel-input .ad {
          background-position: -48px -24px;
        }
        .react-tel-input .ae {
          background-position: -72px -24px;
        }
        .react-tel-input .af {
          background-position: -96px -24px;
        }
        .react-tel-input .ag {
          background-position: -120px -24px;
        }
        .react-tel-input .ai {
          background-position: -144px -24px;
        }
        .react-tel-input .al {
          background-position: -168px -24px;
        }
        .react-tel-input .am {
          background-position: -192px -24px;
        }
        .react-tel-input .an {
          background-position: -216px -24px;
        }
        .react-tel-input .ao {
          background-position: -240px -24px;
        }
        .react-tel-input .aq {
          background-position: -264px -24px;
        }
        .react-tel-input .ar {
          background-position: -288px -24px;
        }
        .react-tel-input .as {
          background-position: -312px -24px;
        }
        .react-tel-input .at {
          background-position: -336px -24px;
        }
        .react-tel-input .au {
          background-position: -360px -24px;
        }
        .react-tel-input .aw {
          background-position: -384px -24px;
        }
        .react-tel-input .ax {
          background-position: -0px -48px;
        }
        .react-tel-input .az {
          background-position: -24px -48px;
        }
        .react-tel-input .ba {
          background-position: -48px -48px;
        }
        .react-tel-input .bb {
          background-position: -72px -48px;
        }
        .react-tel-input .bd {
          background-position: -96px -48px;
        }
        .react-tel-input .be {
          background-position: -120px -48px;
        }
        .react-tel-input .bf {
          background-position: -144px -48px;
        }
        .react-tel-input .bg {
          background-position: -168px -48px;
        }
        .react-tel-input .bh {
          background-position: -192px -48px;
        }
        .react-tel-input .bi {
          background-position: -216px -48px;
        }
        .react-tel-input .bj {
          background-position: -240px -48px;
        }
        .react-tel-input .bl {
          background-position: -264px -48px;
        }
        .react-tel-input .bm {
          background-position: -288px -48px;
        }
        .react-tel-input .bn {
          background-position: -312px -48px;
        }
        .react-tel-input .bo {
          background-position: -336px -48px;
        }
        .react-tel-input .br {
          background-position: -360px -48px;
        }
        .react-tel-input .bs {
          background-position: -384px -48px;
        }
        .react-tel-input .bt {
          background-position: -0px -72px;
        }
        .react-tel-input .bw {
          background-position: -24px -72px;
        }
        .react-tel-input .by {
          background-position: -48px -72px;
        }
        .react-tel-input .bz {
          background-position: -72px -72px;
        }
        .react-tel-input .ca {
          background-position: -96px -72px;
        }
        .react-tel-input .cc {
          background-position: -120px -72px;
        }
        .react-tel-input .cd {
          background-position: -144px -72px;
        }
        .react-tel-input .cf {
          background-position: -168px -72px;
        }
        .react-tel-input .cg {
          background-position: -192px -72px;
        }
        .react-tel-input .ch {
          background-position: -216px -72px;
        }
        .react-tel-input .ci {
          background-position: -240px -72px;
        }
        .react-tel-input .ck {
          background-position: -264px -72px;
        }
        .react-tel-input .cl {
          background-position: -288px -72px;
        }
        .react-tel-input .cm {
          background-position: -312px -72px;
        }
        .react-tel-input .cn {
          background-position: -336px -72px;
        }
        .react-tel-input .co {
          background-position: -360px -72px;
        }
        .react-tel-input .cr {
          background-position: -384px -72px;
        }
        .react-tel-input .cu {
          background-position: -0px -96px;
        }
        .react-tel-input .cv {
          background-position: -24px -96px;
        }
        .react-tel-input .cw {
          background-position: -48px -96px;
        }
        .react-tel-input .cx {
          background-position: -72px -96px;
        }
        .react-tel-input .cy {
          background-position: -96px -96px;
        }
        .react-tel-input .cz {
          background-position: -120px -96px;
        }
        .react-tel-input .de {
          background-position: -144px -96px;
        }
        .react-tel-input .dj {
          background-position: -168px -96px;
        }
        .react-tel-input .dk {
          background-position: -192px -96px;
        }
        .react-tel-input .dm {
          background-position: -216px -96px;
        }
        .react-tel-input .do {
          background-position: -240px -96px;
        }
        .react-tel-input .dz {
          background-position: -264px -96px;
        }
        .react-tel-input .ec {
          background-position: -288px -96px;
        }
        .react-tel-input .ee {
          background-position: -312px -96px;
        }
        .react-tel-input .eg {
          background-position: -336px -96px;
        }
        .react-tel-input .eh {
          background-position: -360px -96px;
        }
        .react-tel-input .er {
          background-position: -384px -96px;
        }
        .react-tel-input .es {
          background-position: -0px -120px;
        }
        .react-tel-input .et {
          background-position: -24px -120px;
        }
        .react-tel-input .eu {
          background-position: -48px -120px;
        }
        .react-tel-input .fi {
          background-position: -72px -120px;
        }
        .react-tel-input .fj {
          background-position: -96px -120px;
        }
        .react-tel-input .fk {
          background-position: -120px -120px;
        }
        .react-tel-input .fm {
          background-position: -144px -120px;
        }
        .react-tel-input .fo {
          background-position: -168px -120px;
        }
        .react-tel-input .fr {
          background-position: -192px -120px;
        }
        .react-tel-input .ga {
          background-position: -216px -120px;
        }
        .react-tel-input .gb {
          background-position: -240px -120px;
        }
        .react-tel-input .gd {
          background-position: -264px -120px;
        }
        .react-tel-input .ge {
          background-position: -288px -120px;
        }
        .react-tel-input .gg {
          background-position: -312px -120px;
        }
        .react-tel-input .gh {
          background-position: -336px -120px;
        }
        .react-tel-input .gi {
          background-position: -360px -120px;
        }
        .react-tel-input .gl {
          background-position: -384px -120px;
        }
        .react-tel-input .gm {
          background-position: -0px -144px;
        }
        .react-tel-input .gn {
          background-position: -24px -144px;
        }
        .react-tel-input .gq {
          background-position: -48px -144px;
        }
        .react-tel-input .gr {
          background-position: -72px -144px;
        }
        .react-tel-input .gs {
          background-position: -96px -144px;
        }
        .react-tel-input .gt {
          background-position: -120px -144px;
        }
        .react-tel-input .gu {
          background-position: -144px -144px;
        }
        .react-tel-input .gw {
          background-position: -168px -144px;
        }
        .react-tel-input .gy {
          background-position: -192px -144px;
        }
        .react-tel-input .hk {
          background-position: -216px -144px;
        }
        .react-tel-input .hn {
          background-position: -240px -144px;
        }
        .react-tel-input .hr {
          background-position: -264px -144px;
        }
        .react-tel-input .ht {
          background-position: -288px -144px;
        }
        .react-tel-input .hu {
          background-position: -312px -144px;
        }
        .react-tel-input .ic {
          background-position: -336px -144px;
        }
        .react-tel-input .id {
          background-position: -360px -144px;
        }
        .react-tel-input .ie {
          background-position: -384px -144px;
        }
        .react-tel-input .il {
          background-position: -0px -168px;
        }
        .react-tel-input .im {
          background-position: -24px -168px;
        }
        .react-tel-input .in {
          background-position: -48px -168px;
        }
        .react-tel-input .iq {
          background-position: -72px -168px;
        }
        .react-tel-input .ir {
          background-position: -96px -168px;
        }
        .react-tel-input .is {
          background-position: -120px -168px;
        }
        .react-tel-input .it {
          background-position: -144px -168px;
        }
        .react-tel-input .je {
          background-position: -168px -168px;
        }
        .react-tel-input .jm {
          background-position: -192px -168px;
        }
        .react-tel-input .jo {
          background-position: -216px -168px;
        }
        .react-tel-input .jp {
          background-position: -240px -168px;
        }
        .react-tel-input .ke {
          background-position: -264px -168px;
        }
        .react-tel-input .kg {
          background-position: -288px -168px;
        }
        .react-tel-input .kh {
          background-position: -312px -168px;
        }
        .react-tel-input .ki {
          background-position: -336px -168px;
        }
        .react-tel-input .km {
          background-position: -360px -168px;
        }
        .react-tel-input .kn {
          background-position: -384px -168px;
        }
        .react-tel-input .kp {
          background-position: -0px -192px;
        }
        .react-tel-input .kr {
          background-position: -24px -192px;
        }
        .react-tel-input .kw {
          background-position: -48px -192px;
        }
        .react-tel-input .ky {
          background-position: -72px -192px;
        }
        .react-tel-input .kz {
          background-position: -96px -192px;
        }
        .react-tel-input .la {
          background-position: -120px -192px;
        }
        .react-tel-input .lb {
          background-position: -144px -192px;
        }
        .react-tel-input .lc {
          background-position: -168px -192px;
        }
        .react-tel-input .li {
          background-position: -192px -192px;
        }
        .react-tel-input .lk {
          background-position: -216px -192px;
        }
        .react-tel-input .lr {
          background-position: -240px -192px;
        }
        .react-tel-input .ls {
          background-position: -264px -192px;
        }
        .react-tel-input .lt {
          background-position: -288px -192px;
        }
        .react-tel-input .lu {
          background-position: -312px -192px;
        }
        .react-tel-input .lv {
          background-position: -336px -192px;
        }
        .react-tel-input .ly {
          background-position: -360px -192px;
        }
        .react-tel-input .ma {
          background-position: -384px -192px;
        }
        .react-tel-input .mc {
          background-position: -0px -216px;
        }
        .react-tel-input .md {
          background-position: -24px -216px;
        }
        .react-tel-input .me {
          background-position: -48px -216px;
        }
        .react-tel-input .mf {
          background-position: -72px -216px;
        }
        .react-tel-input .mg {
          background-position: -96px -216px;
        }
        .react-tel-input .mh {
          background-position: -120px -216px;
        }
        .react-tel-input .mk {
          background-position: -144px -216px;
        }
        .react-tel-input .ml {
          background-position: -168px -216px;
        }
        .react-tel-input .mm {
          background-position: -192px -216px;
        }
        .react-tel-input .mn {
          background-position: -216px -216px;
        }
        .react-tel-input .mo {
          background-position: -240px -216px;
        }
        .react-tel-input .mp {
          background-position: -264px -216px;
        }
        .react-tel-input .mq {
          background-position: -288px -216px;
        }
        .react-tel-input .mr {
          background-position: -312px -216px;
        }
        .react-tel-input .ms {
          background-position: -336px -216px;
        }
        .react-tel-input .mt {
          background-position: -360px -216px;
        }
        .react-tel-input .mu {
          background-position: -384px -216px;
        }
        .react-tel-input .mv {
          background-position: -0px -240px;
        }
        .react-tel-input .mw {
          background-position: -24px -240px;
        }
        .react-tel-input .mx {
          background-position: -48px -240px;
        }
        .react-tel-input .my {
          background-position: -72px -240px;
        }
        .react-tel-input .mz {
          background-position: -96px -240px;
        }
        .react-tel-input .na {
          background-position: -120px -240px;
        }
        .react-tel-input .nc {
          background-position: -144px -240px;
        }
        .react-tel-input .ne {
          background-position: -168px -240px;
        }
        .react-tel-input .nf {
          background-position: -192px -240px;
        }
        .react-tel-input .ng {
          background-position: -216px -240px;
        }
        .react-tel-input .ni {
          background-position: -240px -240px;
        }
        .react-tel-input .nl {
          background-position: -264px -240px;
        }
        .react-tel-input .no {
          background-position: -288px -240px;
        }
        .react-tel-input .np {
          background-position: -312px -240px;
        }
        .react-tel-input .nr {
          background-position: -336px -240px;
        }
        .react-tel-input .nu {
          background-position: -360px -240px;
        }
        .react-tel-input .nz {
          background-position: -384px -240px;
        }
        .react-tel-input .om {
          background-position: -0px -264px;
        }
        .react-tel-input .pa {
          background-position: -24px -264px;
        }
        .react-tel-input .pe {
          background-position: -48px -264px;
        }
        .react-tel-input .pf {
          background-position: -72px -264px;
        }
        .react-tel-input .pg {
          background-position: -96px -264px;
        }
        .react-tel-input .ph {
          background-position: -120px -264px;
        }
        .react-tel-input .pk {
          background-position: -192px -264px;
        }
        .react-tel-input .pl {
          background-position: -216px -264px;
        }
        .react-tel-input .pn {
          background-position: -240px -264px;
        }
        .react-tel-input .pr {
          background-position: -264px -264px;
        }
        .react-tel-input .ps {
          background-position: -288px -264px;
        }
        .react-tel-input .pt {
          background-position: -312px -264px;
        }
        .react-tel-input .pw {
          background-position: -336px -264px;
        }
        .react-tel-input .py {
          background-position: -360px -264px;
        }
        .react-tel-input .qa {
          background-position: -384px -264px;
        }
        .react-tel-input .ro {
          background-position: -0px -288px;
        }
        .react-tel-input .rs {
          background-position: -24px -288px;
        }
        .react-tel-input .ru {
          background-position: -48px -288px;
        }
        .react-tel-input .rw {
          background-position: -72px -288px;
        }
        .react-tel-input .sa {
          background-position: -96px -288px;
        }
        .react-tel-input .sb {
          background-position: -120px -288px;
        }
        .react-tel-input .sc {
          background-position: -144px -288px;
        }
        .react-tel-input .sd {
          background-position: -168px -288px;
        }
        .react-tel-input .se {
          background-position: -192px -288px;
        }
        .react-tel-input .sg {
          background-position: -216px -288px;
        }
        .react-tel-input .sh {
          background-position: -240px -288px;
        }
        .react-tel-input .si {
          background-position: -264px -288px;
        }
        .react-tel-input .sk {
          background-position: -288px -288px;
        }
        .react-tel-input .sl {
          background-position: -312px -288px;
        }
        .react-tel-input .sm {
          background-position: -336px -288px;
        }
        .react-tel-input .sn {
          background-position: -360px -288px;
        }
        .react-tel-input .so {
          background-position: -384px -288px;
        }
        .react-tel-input .sr {
          background-position: -0px -312px;
        }
        .react-tel-input .ss {
          background-position: -24px -312px;
        }
        .react-tel-input .st {
          background-position: -48px -312px;
        }
        .react-tel-input .sv {
          background-position: -72px -312px;
        }
        .react-tel-input .sy {
          background-position: -96px -312px;
        }
        .react-tel-input .sz {
          background-position: -120px -312px;
        }
        .react-tel-input .tc {
          background-position: -144px -312px;
        }
        .react-tel-input .td {
          background-position: -168px -312px;
        }
        .react-tel-input .tf {
          background-position: -192px -312px;
        }
        .react-tel-input .tg {
          background-position: -216px -312px;
        }
        .react-tel-input .th {
          background-position: -240px -312px;
        }
        .react-tel-input .tj {
          background-position: -264px -312px;
        }
        .react-tel-input .tk {
          background-position: -288px -312px;
        }
        .react-tel-input .tl {
          background-position: -312px -312px;
        }
        .react-tel-input .tm {
          background-position: -336px -312px;
        }
        .react-tel-input .tn {
          background-position: -360px -312px;
        }
        .react-tel-input .to {
          background-position: -384px -312px;
        }
        .react-tel-input .tr {
          background-position: -0px -336px;
        }
        .react-tel-input .tt {
          background-position: -24px -336px;
        }
        .react-tel-input .tv {
          background-position: -48px -336px;
        }
        .react-tel-input .tw {
          background-position: -72px -336px;
        }
        .react-tel-input .tz {
          background-position: -96px -336px;
        }
        .react-tel-input .ua {
          background-position: -120px -336px;
        }
        .react-tel-input .ug {
          background-position: -144px -336px;
        }
        .react-tel-input .us {
          background-position: -168px -336px;
        }
        .react-tel-input .uy {
          background-position: -192px -336px;
        }
        .react-tel-input .uz {
          background-position: -216px -336px;
        }
        .react-tel-input .va {
          background-position: -240px -336px;
        }
        .react-tel-input .vc {
          background-position: -264px -336px;
        }
        .react-tel-input .ve {
          background-position: -288px -336px;
        }
        .react-tel-input .vg {
          background-position: -312px -336px;
        }
        .react-tel-input .vi {
          background-position: -336px -336px;
        }
        .react-tel-input .vn {
          background-position: -360px -336px;
        }
        .react-tel-input .vu {
          background-position: -384px -336px;
        }
        .react-tel-input .hide {
          display: none;
        }
        .react-tel-input .v-hide {
          visibility: hidden;
        }
        .react-tel-input .selected-flag .arrow {
          position: absolute;
          top: calc(50% - 6px);
          right: 16px;
          width: 8px;
          height: 8px;
          border-style: solid;
          border-color: ${theme.palette.gray[900]};
          border-width: 0 1.5px 1.5px 0;
          transform: rotate(45deg);
        }
        .react-tel-input .form-control:disabled + .flag-dropdown .selected-flag .arrow {
          border-color: ${theme.palette.gray[400]};
        }
        .react-tel-input .selected-flag .arrow.up {
          top: calc(50% - 2px);
          transform: rotate(-135deg);
        }
        .react-tel-input .flag-dropdown.open .country-list {
          z-index: 2;
        }
        .react-tel-input .country-list {
          position: absolute;
          box-shadow: 0px 2px 5px 0px rgba(16, 24, 40, 0.1), 0px 9px 25px 0px rgba(16, 24, 40, 0.15);
          border: 1px solid ${theme.palette.gray[200]};
          background-color: ${theme.palette.background.default};
          border-radius: 5px;
          width: auto;
          max-height: 190px;
          overflow: hidden;
          overflow-y: auto;
          margin-top: ${theme.spacing[1]};
        }
        .react-tel-input .country-list::-webkit-scrollbar-track {
          width: 5px;
          background-color: transparent;
        }
        .react-tel-input .country-list::-webkit-scrollbar {
          width: 5px;
          background-color: transparent;
        }
        .react-tel-input .country-list::-webkit-scrollbar-thumb {
          width: 5px;
          height: 80px;
          border-radius: 5px;
          background-color: ${theme.palette.gray[300]};
        }
        .react-tel-input .country-list .country {
          display: flex;
          align-items: center;
          position: relative;
          padding: ${theme.spacing[1.5]} ${theme.spacing[5.5]} ${theme.spacing[1.5]}
            ${theme.spacing[2.5]};
        }
        .react-tel-input .country-list .country:last-child {
          margin-bottom: ${theme.spacing[1]};
        }
        .react-tel-input .country-list .flag {
          display: none;
        }
        .react-tel-input .country-list .country-name {
          ${theme.typography.text14.desktop};
          margin-right: ${theme.spacing[2]};
        }
        .react-tel-input .country-list .country .dial-code {
          ${theme.typography.text14.desktop};
          color: ${theme.palette.text.light};
        }
        .react-tel-input .country-list .country:hover,
        .react-tel-input .country-list .country:focus {
          background-color: ${theme.palette.gray[100]};
        }
        .react-tel-input .country-list .divider {
          margin: ${theme.spacing[1]} 0;
          border-bottom: 1px solid ${theme.palette.gray[200]};
        }
        .no-entries-message {
          color: ${theme.palette.text.light};
          margin-left: ${theme.spacing[2.5]};
          margin-top: ${theme.spacing[1]};
          margin-bottom: ${theme.spacing[2]};
          ${theme.typography.text14.desktop};
          cursor: default;
        }
        .search input {
          ${theme.typography.text14.desktop};
          padding: ${theme.spacing[2]} ${theme.spacing[2.5]};
          width: 100%;
          border-bottom: 1px solid ${theme.palette.gray[200]};
          margin-bottom: ${theme.spacing[1]};
        }
        .react-tel-input ::placeholder {
          ${theme.typography.text14.desktop};
        }
        .react-tel-input ::-webkit-input-placeholder {
          ${theme.typography.text14.desktop};
        }
        .react-tel-input ::-moz-placeholder {
          ${theme.typography.text14.desktop};
        }
        .react-tel-input :-ms-input-placeholder {
          ${theme.typography.text14.desktop};
        }
        .react-tel-input :-moz-placeholder {
          ${theme.typography.text14.desktop};
        }
      `}</style>

      {/* PhoneInput only marks the keyboard highlight, which moves to the first search result */}
      <style jsx global>{`
        .react-tel-input.selected-${country}
          .country-list
          .country[data-country-code='${country}']::after {
          content: '';
          width: 16px;
          height: 16px;
          background-image: url('/images/icons/checkmark-blue.svg');
          background-size: 18px;
          position: absolute;
          right: 12px;
          background-repeat: no-repeat;
          background-position: center;
        }
      `}</style>
    </>
  )
}
